// src/services/firebase.js
import { initializeApp } from 'firebase/app';
import { initializeAppCheck, ReCaptchaEnterpriseProvider } from 'firebase/app-check';
import {
  getAuth, RecaptchaVerifier, signInWithPhoneNumber, PhoneAuthProvider,
  signInWithCredential, signOut, onAuthStateChanged,
} from 'firebase/auth';
import { registerPlugin, Capacitor } from '@capacitor/core';
import { FirebaseAuthentication } from '@capacitor-firebase/authentication';
import { isNativeCustomerShell, useNativePhoneAuth } from '../utils/platform';
import {
  clearDeliveryTarget, clearDeliverySession, clearPendingLocationPrompt,
} from '../utils/deliveryTarget';
import { getMessaging, getToken, onMessage, isSupported } from 'firebase/messaging';
import { getFirestore, collection, doc, getDoc, setDoc, updateDoc, onSnapshot, query, where, serverTimestamp } from 'firebase/firestore';

const FCM_VAPID_KEY = process.env.REACT_APP_FCM_VAPID_KEY
  || 'BFE1Pa4va663rmeBebHMlB_UUU1R3t-TkUAMq2I6jjxarW93PQpvCiVXeG55u606vzKnj7qMaJHse7gvG48YUgo';

// ─── ENV VALIDATION ──────────────────────────
const REQUIRED_ENV = [
  'REACT_APP_FIREBASE_API_KEY',
  'REACT_APP_FIREBASE_AUTH_DOMAIN',
  'REACT_APP_FIREBASE_PROJECT_ID',
  'REACT_APP_FIREBASE_APP_ID',
  'REACT_APP_FIREBASE_DATABASE_URL',
];
const missingEnv = REQUIRED_ENV.filter((k) => !process.env[k]);
if (missingEnv.length) {
  console.error(`Missing env vars: ${missingEnv.join(', ')}. Copy .env.example to .env and fill in your Firebase keys.`);
}

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  databaseURL: process.env.REACT_APP_FIREBASE_DATABASE_URL,
};

const app = initializeApp(firebaseConfig);

// App Check — opt-in only (REACT_APP_ENABLE_APP_CHECK=true after domains registered)
const appCheckEnabled = process.env.REACT_APP_ENABLE_APP_CHECK === 'true';
const recaptchaKey = process.env.REACT_APP_RECAPTCHA_ENTERPRISE_KEY;
if (process.env.NODE_ENV === 'development' && process.env.REACT_APP_APPCHECK_DEBUG_TOKEN) {
  self.FIREBASE_APPCHECK_DEBUG_TOKEN = process.env.REACT_APP_APPCHECK_DEBUG_TOKEN;
}
if (appCheckEnabled && recaptchaKey) {
  initializeAppCheck(app, {
    provider: new ReCaptchaEnterpriseProvider(recaptchaKey),
    isTokenAutoRefreshEnabled: true,
  });
} else if (appCheckEnabled && !recaptchaKey) {
  console.warn('REACT_APP_ENABLE_APP_CHECK is true but REACT_APP_RECAPTCHA_ENTERPRISE_KEY is missing.');
}

export { app };
export const auth = getAuth(app);
export const db = getFirestore(app);

/** Native bridge only — register once on native; web uses package export (no double-register). */
let firebaseAuthNativePlugin = null;
const getNativeAuthPlugin = () => {
  if (!firebaseAuthNativePlugin) {
    firebaseAuthNativePlugin = registerPlugin('FirebaseAuthentication');
  }
  return firebaseAuthNativePlugin;
};

const getPhoneAuthPlugin = () => (
  useNativePhoneAuth() ? getNativeAuthPlugin() : FirebaseAuthentication
);

const nativePhoneAuthCall = async (method, options) => {
  if (useNativePhoneAuth() && typeof Capacitor.nativePromise === 'function') {
    return Capacitor.nativePromise('FirebaseAuthentication', method, options);
  }
  return getPhoneAuthPlugin()[method](options);
};

// ─── NATIVE PHONE AUTH (Capacitor / Play Store) ─
let nativeVerificationId = null;
let nativePhoneError = null;
let nativePhoneListenersReady = false;
let nativeOtpAttempt = 0;
let nativeOtpCallbacks = { onAutoVerified: null, onCodeReceived: null };

const waitForAuthUser = (maxMs = 4000) => new Promise((resolve) => {
  if (auth.currentUser) {
    resolve(auth.currentUser);
    return;
  }
  const timeout = setTimeout(() => {
    unsub();
    resolve(null);
  }, maxMs);
  const unsub = onAuthStateChanged(auth, (user) => {
    if (user) {
      clearTimeout(timeout);
      unsub();
      resolve(user);
    }
  });
});

const signInWithNativeCredential = async (verificationId, code) => {
  const credential = PhoneAuthProvider.credential(verificationId, code);
  const result = await signInWithCredential(auth, credential);
  nativeVerificationId = null;
  return result.user;
};

export const setNativeOtpCallbacks = (callbacks = {}) => {
  nativeOtpCallbacks = { ...nativeOtpCallbacks, ...callbacks };
};

const waitForNativeVerificationId = (attemptId) => new Promise((resolve, reject) => {
  const started = Date.now();
  const poll = setInterval(() => {
    if (attemptId !== nativeOtpAttempt) {
      clearInterval(poll);
      reject(Object.assign(new Error('OTP request superseded'), { code: 'auth/superseded' }));
      return;
    }
    if (auth.currentUser) {
      clearInterval(poll);
      resolve({ autoVerified: true });
    } else if (nativeVerificationId) {
      clearInterval(poll);
      resolve({ verificationId: nativeVerificationId });
    } else if (nativePhoneError) {
      clearInterval(poll);
      const err = nativePhoneError;
      nativePhoneError = null;
      reject(err);
    } else if (Date.now() - started > 90000) {
      clearInterval(poll);
      reject(Object.assign(new Error('OTP request timed out'), { code: 'auth/timeout' }));
    }
  }, 150);
});

const ensureNativePhoneListeners = async () => {
  if (nativePhoneListenersReady) return;
  if (!useNativePhoneAuth()) return;
  await Promise.all([
    getPhoneAuthPlugin().addListener('phoneCodeSent', (event) => {
      nativeVerificationId = event.verificationId;
    }),
    getPhoneAuthPlugin().addListener('phoneVerificationCompleted', async (event) => {
      if (event.verificationCode && nativeVerificationId) {
        try {
          await signInWithNativeCredential(nativeVerificationId, event.verificationCode);
          nativeOtpCallbacks.onCodeReceived?.(event.verificationCode);
        } catch (e) {
          nativePhoneError = e;
        }
        return;
      }
      if (event.verificationCode) {
        nativeOtpCallbacks.onCodeReceived?.(event.verificationCode);
        return;
      }
      const user = await waitForAuthUser();
      if (user) {
        nativeVerificationId = null;
        nativeOtpCallbacks.onAutoVerified?.();
        return;
      }
      nativePhoneError = Object.assign(new Error('Phone auto-verification failed'), {
        code: 'auth/auto-verify-failed',
      });
    }),
    getPhoneAuthPlugin().addListener('phoneVerificationFailed', (event) => {
      const raw = event?.message || event?.code || 'Phone verification failed';
      nativePhoneError = Object.assign(new Error(raw), {
        code: event?.code || raw,
      });
    }),
  ]);
  nativePhoneListenersReady = true;
};

/** Call once on app start so listeners are ready before login */
export const initNativePhoneAuth = () => {
  if (useNativePhoneAuth()) {
    ensureNativePhoneListeners().catch((e) => console.warn('Native phone auth init:', e?.message));
  }
};

// ─── AUTH HELPERS ─────────────────────────────
const AUTH_ERROR_KEYS = {
  'auth/invalid-phone-number': 'authErrors.invalidPhone',
  'auth/too-many-requests': 'authErrors.tooManyRequests',
  'auth/captcha-check-failed': 'authErrors.captchaFailed',
  'auth/argument-error': 'authErrors.captchaFailed',
  'auth/invalid-verification-code': 'authErrors.invalidOtp',
  'auth/code-expired': 'authErrors.codeExpired',
  'auth/missing-verification-code': 'authErrors.missingOtp',
  'auth/quota-exceeded': 'authErrors.quotaExceeded',
  'auth/app-not-authorized': 'authErrors.appNotAuthorized',
  'auth/network-request-failed': 'authErrors.networkFailed',
  'auth/missing-otp-session': 'authErrors.noOtpSession',
  'auth/native-shell-required': 'authErrors.appNotAuthorized',
  'auth/referer-blocked': 'authErrors.refererBlocked',
  'auth/recaptcha-script-failed': 'authErrors.networkFailed',
};

const NATIVE_AUTH_HINTS = [
  'invalid-cert-hash', 'invalid cert', 'not authorized', 'app identifier',
  'missing a valid app', '17499', '17093', '12500', 'app-not-authorized',
];

export const getAuthErrorKey = (error) => {
  const code = error?.code || '';
  const msg = (error?.message || '').toLowerCase();
  const combined = `${code} ${msg}`.toLowerCase();
  if (AUTH_ERROR_KEYS[code]) return AUTH_ERROR_KEYS[code];
  if (NATIVE_AUTH_HINTS.some((hint) => combined.includes(hint))) {
    return 'authErrors.appNotAuthorized';
  }
  if (combined.includes('timeout')) return 'authErrors.timeout';
  if (combined.includes('network')) return 'authErrors.networkFailed';
  if (combined.includes('too-many') || combined.includes('too many')) return 'authErrors.tooManyRequests';
  if (combined.includes('quota')) return 'authErrors.quotaExceeded';
  if (msg.includes('recaptcha') || msg.includes('referer')) {
    return 'authErrors.refererBlocked';
  }
  if (msg.includes('no otp session')) return 'authErrors.noOtpSession';
  if (combined.includes('permission-denied') || combined.includes('insufficient permissions')) {
    return 'authErrors.profilePermission';
  }
  return 'authErrors.default';
};

/** @deprecated Use getAuthErrorKey + t() in components */
export const getAuthErrorMessage = (error) => {
  const code = error?.code || '';
  const messages = {
    'auth/invalid-phone-number': 'Invalid phone number. Enter a valid 10-digit Indian mobile number.',
    'auth/too-many-requests': 'Too many attempts. Please wait a few minutes and try again.',
    'auth/captcha-check-failed': 'Security check failed. Refresh the page and try again.',
    'auth/invalid-verification-code': 'Wrong OTP. Please check the code and try again.',
    'auth/code-expired': 'OTP expired. Tap Resend OTP to get a new one.',
    'auth/missing-verification-code': 'Enter the complete 6-digit OTP.',
    'auth/quota-exceeded': 'SMS quota exceeded. Contact support or try again later.',
    'auth/app-not-authorized': 'This app is not authorized for phone auth. Add your domain in Firebase Console → Authentication → Settings → Authorized domains.',
    'auth/network-request-failed': 'Network error. Check your internet connection.',
  };
  return messages[code]
    || (code.includes('requests-from-referer') || error?.message?.includes('referer')
      ? 'This site is blocked by your Firebase API key. In Google Cloud Console → Credentials, add your hosting URL to HTTP referrers (see deploy notes).'
      : null)
    || error?.message || 'Something went wrong. Please try again.';
};

const ensureRecaptchaContainer = () => {
  document.querySelectorAll('[id^="recaptcha-container"]').forEach((el) => el.remove());
  const el = document.createElement('div');
  el.id = `recaptcha-container-${Date.now()}`;
  el.setAttribute('aria-hidden', 'true');
  el.style.cssText = 'position:fixed;left:-9999px;width:1px;height:1px;overflow:hidden;pointer-events:none';
  document.body.appendChild(el);
  return el.id;
};

const loadRecaptchaScript = () => {
  if (typeof window !== 'undefined' && window.grecaptcha) return Promise.resolve();
  if (window.recaptchaScriptPromise) return window.recaptchaScriptPromise;
  window.recaptchaScriptPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector('script[data-mytownly-recaptcha]');
    if (existing) {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', () => reject(new Error('reCAPTCHA script failed to load')));
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://www.google.com/recaptcha/api.js?render=explicit';
    script.async = true;
    script.defer = true;
    script.dataset.mytownlyRecaptcha = '1';
    script.onload = () => resolve();
    script.onerror = () => {
      window.recaptchaScriptPromise = null;
      reject(Object.assign(new Error('Could not load reCAPTCHA. Check your connection or try the MyTownly app.'), {
        code: 'auth/recaptcha-script-failed',
      }));
    };
    document.head.appendChild(script);
  });
  return window.recaptchaScriptPromise;
};

const destroyRecaptchaWidget = async () => {
  if (window.recaptchaVerifier) {
    try { await window.recaptchaVerifier.clear(); } catch (e) { /* already cleared */ }
    window.recaptchaVerifier = null;
  }
  window.recaptchaWidgetId = null;
  document.querySelectorAll('[id^="recaptcha-container"]').forEach((el) => el.remove());
};

const getRecaptchaVerifier = async (forceNew = false) => {
  if (useNativePhoneAuth()) {
    throw Object.assign(new Error('reCAPTCHA not used in native app'), { code: 'auth/native-no-recaptcha' });
  }
  if (forceNew || window.recaptchaVerifier) {
    await destroyRecaptchaWidget();
  }
  await loadRecaptchaScript();
  const containerId = ensureRecaptchaContainer();
  window.recaptchaVerifier = new RecaptchaVerifier(auth, containerId, {
    size: 'invisible',
    callback: () => {},
    'expired-callback': () => {
      window.recaptchaVerifier = null;
      window.confirmationResult = null;
    },
  });
  try {
    window.recaptchaWidgetId = await window.recaptchaVerifier.render();
  } catch (e) {
    await destroyRecaptchaWidget();
    const msg = (e?.message || '').toLowerCase();
    if (msg.includes('network') || msg.includes('failed') || msg.includes('recaptchaparams')) {
      throw Object.assign(new Error(
        'Phone sign-in security check failed. If you are on the website, add app.mytownly.in to your Firebase API key HTTP referrers in Google Cloud Console. Or use the MyTownly Android app.',
      ), { code: 'auth/referer-blocked' });
    }
    throw e;
  }
  return window.recaptchaVerifier;
};

/** Preload invisible reCAPTCHA while user enters phone — saves 1–3s on Send OTP. */
export const warmRecaptcha = () => {
  if (useNativePhoneAuth()) return Promise.resolve();
  if (window.recaptchaWarmPromise) return window.recaptchaWarmPromise;
  window.recaptchaWarmPromise = getRecaptchaVerifier()
    .catch(() => { window.recaptchaWarmPromise = null; });
  return window.recaptchaWarmPromise;
};

export const sendOtp = async (phoneNumber) => {
  window.confirmationResult = null;
  if (isNativeCustomerShell() && !useNativePhoneAuth()) {
    throw Object.assign(new Error('Native sign-in is not ready. Close and reopen the app.'), {
      code: 'auth/native-shell-required',
    });
  }
  if (useNativePhoneAuth()) {
    await ensureNativePhoneListeners();
    nativeOtpAttempt += 1;
    const attemptId = nativeOtpAttempt;
    nativeVerificationId = null;
    nativePhoneError = null;
    try {
      await nativePhoneAuthCall('signInWithPhoneNumber', {
        phoneNumber,
        timeout: 60,
        skipNativeAuth: true,
      });
      const waitResult = await waitForNativeVerificationId(attemptId);
      if (waitResult.autoVerified) return { autoVerified: true };
      return { native: true };
    } catch (e) {
      console.error('Native OTP failed:', e);
      throw Object.assign(new Error(e.message || 'Native OTP failed'), {
        code: e.code || 'auth/native-otp-failed',
      });
    }
  }
  if (isNativeCustomerShell()) {
    throw Object.assign(new Error('Phone sign-in failed in the app. Restart and try again.'), {
      code: 'auth/native-shell-required',
    });
  }
  const verifier = await (window.recaptchaWarmPromise || getRecaptchaVerifier());
  window.recaptchaWarmPromise = null;
  const confirmation = await signInWithPhoneNumber(auth, phoneNumber, verifier);
  window.confirmationResult = confirmation;
  return confirmation;
};

export const verifyOtp = async (otp) => {
  if (auth.currentUser && !nativeVerificationId && !window.confirmationResult) {
    return auth.currentUser;
  }
  if (useNativePhoneAuth() && nativeVerificationId) {
    const verificationId = nativeVerificationId;
    nativeVerificationId = null;
    try {
      await nativePhoneAuthCall('confirmVerificationCode', {
        verificationId,
        verificationCode: otp,
      });
      const synced = await waitForAuthUser();
      if (synced) return synced;
    } catch (pluginErr) {
      console.warn('Native confirmVerificationCode:', pluginErr?.message);
    }
    return signInWithNativeCredential(verificationId, otp);
  }
  if (!window.confirmationResult) {
    const err = new Error('No OTP session. Please request a new OTP.');
    err.code = 'auth/missing-otp-session';
    throw err;
  }
  try {
    const result = await window.confirmationResult.confirm(otp);
    window.confirmationResult = null;
    return result.user;
  } catch (e) {
    if (e?.code === 'auth/invalid-verification-code' || e?.code === 'auth/code-expired') {
      window.confirmationResult = null;
    }
    throw e;
  }
};

export const logout = async () => {
  clearDeliveryTarget();
  clearDeliverySession();
  clearPendingLocationPrompt();
  await signOut(auth);
};

// ─── USERS ────────────────────────────────────
export const createOrUpdateUser = async (uid, data) => updateUserProfile(uid, data);

export const getUser = async (uid) => {
  const snap = await getDoc(doc(db, 'users', uid));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
};

export const listenToUserProfile = (uid, callback, onError) => {
  return onSnapshot(
    doc(db, 'users', uid),
    (snap) => callback(snap.exists() ? { id: snap.id, ...snap.data() } : null),
    (err) => {
      console.warn('User profile listener:', err?.message);
      onError?.(err);
    },
  );
};

export const updateUserProfile = async (uid, data) => {
  const allowed = ['name', 'profileComplete', 'addresses', 'interests', 'fcmToken', 'phone'];
  const patch = {};
  allowed.forEach((key) => {
    if (Object.prototype.hasOwnProperty.call(data, key)) patch[key] = data[key];
  });
  if (!Object.keys(patch).length) return;
  await setDoc(doc(db, 'users', uid), { ...patch, updatedAt: serverTimestamp() }, { merge: true });
};

export const ensureUserDefaults = async (uid, phone) => {
  const existing = await getUser(uid);
  const phoneStr = phone ? String(phone).slice(0, 20) : null;
  const referralCode = existing?.referralCode || `PILER${uid.slice(0, 6).toUpperCase()}`;

  if (existing) {
    if (!existing.phone && phoneStr) {
      await updateUserProfile(uid, { phone: phoneStr });
      return { id: uid, ...existing, phone: phoneStr };
    }
    return { id: uid, ...existing };
  }

  const createPayload = {
    referralCode,
    addresses: [],
    interests: [],
    profileComplete: false,
    ...(phoneStr ? { phone: phoneStr } : {}),
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };

  try {
    await setDoc(doc(db, 'users', uid), createPayload, { merge: true });
    return { id: uid, ...createPayload };
  } catch (e) {
    console.warn('Full profile create failed, trying minimal:', e?.message);
    await updateUserProfile(uid, {
      profileComplete: false,
      addresses: [],
      interests: [],
      ...(phoneStr ? { phone: phoneStr } : {}),
    });
    return {
      id: uid,
      referralCode,
      addresses: [],
      interests: [],
      profileComplete: false,
      ...(phoneStr ? { phone: phoneStr } : {}),
    };
  }
};

// ─── GROCERY ──────────────────────────────────
export const getShops = (callback, onError) => {
  const q = query(collection(db, 'shops'), where('active', '==', true));
  return onSnapshot(
    q,
    (snap) => {
      callback(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    },
    (err) => {
      console.warn('Shops listener:', err?.message);
      onError?.(err);
    },
  );
};

export const getProducts = (shopId, callback, onError) => {
  const q = query(collection(db, 'products'), where('shopId', '==', shopId), where('inStock', '==', true));
  return onSnapshot(
    q,
    (snap) => {
      callback(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    },
    (err) => {
      console.warn('Products listener:', err?.message);
      onError?.(err);
    },
  );
};

export const getShopById = async (shopId) => {
  const snap = await getDoc(doc(db, 'shops', shopId));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
};

export const cancelCustomerOrder = async (orderId, reason = 'customer_cancelled') => {
  await updateDoc(doc(db, 'orders', orderId), {
    status: 'cancelled',
    cancellationReason: String(reason).slice(0, 120),
    cancelledAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
};

export const submitRiderRating = async (orderId, rating) => {
  const value = Math.round(Number(rating));
  if (value < 1 || value > 5) throw new Error('Invalid rating');
  await updateDoc(doc(db, 'orders', orderId), {
    riderRating: value,
    riderRatedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
};

export const listenToOrder = (orderId, callback, onError) => {
  return onSnapshot(
    doc(db, 'orders', orderId),
    (snap) => {
      callback(snap.exists() ? { id: snap.id, ...snap.data() } : null);
    },
    (err) => {
      console.warn('Order listener:', err?.message);
      onError?.(err);
    },
  );
};

export const listenToUserOrders = (customerId, callback, onError) => {
  const q = query(collection(db, 'orders'), where('customerId', '==', customerId));
  return onSnapshot(
    q,
    (snap) => {
      const orders = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      orders.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
      callback(orders);
    },
    (err) => {
      console.warn('Orders listener:', err?.message);
      onError?.(err);
      callback([]);
    },
  );
};

// ─── FCM (order status push) ──────────────────
export const registerMessagingSw = async () => {
  if (!('serviceWorker' in navigator)) return null;
  try {
    const reg = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
    await navigator.serviceWorker.ready;
    return reg;
  } catch (e) {
    console.warn('FCM SW:', e.message);
    return null;
  }
};

export const requestNotificationPermission = async () => {
  if (!('Notification' in window)) return 'unsupported';
  if (Notification.permission === 'granted') return 'granted';
  if (Notification.permission === 'denied') return 'denied';
  return Notification.requestPermission();
};

export const saveCustomerFcmToken = async (uid) => {
  try {
    if (!(await isSupported())) return false;
    // Never prompt here — NotificationPrompt handles permission; avoids blocking login.
    if (Notification.permission !== 'granted') return false;

    const reg = await registerMessagingSw();
    if (!reg) return false;

    const m = getMessaging(app);
    const token = await getToken(m, {
      vapidKey: FCM_VAPID_KEY,
      serviceWorkerRegistration: reg,
    });
    if (token) {
      await updateUserProfile(uid, { fcmToken: token });
      return true;
    }
  } catch (e) {
    console.warn('FCM:', e.message);
  }
  return false;
};

export const listenToForegroundMessages = async (callback) => {
  if (!(await isSupported())) return () => {};
  const m = getMessaging(app);
  return onMessage(m, callback);
};

/** Live platform settings from admin panel (config/platform) */
export const listenToPlatformConfig = (callback) => {
  return onSnapshot(doc(db, 'config', 'platform'), (snap) => {
    callback(snap.exists() ? snap.data() : null);
  }, (err) => {
    console.warn('Platform config listener:', err.message);
    callback(null);
  });
};
